import { Skeleton } from "@universe/ui/components/skeleton";
import { useIsMobile } from "@universe/ui/hooks/use-mobile";
import { ArrowUp, Landmark, Repeat } from "lucide-react";
import { useTranslations } from "next-intl";

import {
  getExchangeBuyRoute,
  getExchangeSellRoute,
  getPayRoute,
} from "@/enums/route";
import { useReturnTo } from "@/hooks/useReturnTo";
import { useCustomerAccount } from "@/providers";

import ActionIconButton from "./action-icon-button";
import AnimatedNumber from "./animated-number";
import CurrencyFlag from "./currency-flag";
import DepositButton from "./deposit-button";

interface TotalBalanceProps {
  balance: string | number;
  currency: string;
  isBaseCurrency?: boolean;
  totalBalanceText?: string;
  subtitleText?: string;
  showCurrencyFlag?: boolean;
  showSmallCurrencyFlag?: boolean;
  showDepositAccountSelect?: boolean;
  depositButtonVariant?: "default" | "outline";
  isStillCalculating?: boolean;
  isPayAvailable?: boolean;
  isExchangeAvailable?: boolean;
  isDepositAvailable?: boolean;
  forceEnablePay?: boolean;
}

export default function TotalBalance({
  balance,
  currency,
  isBaseCurrency,
  totalBalanceText,
  subtitleText,
  showCurrencyFlag,
  showSmallCurrencyFlag = false,
  showDepositAccountSelect,
  depositButtonVariant = "default",
  isStillCalculating = false,
  isPayAvailable = true,
  isExchangeAvailable = true,
  isDepositAvailable = true,
  forceEnablePay = false,
}: TotalBalanceProps) {
  const isMobile = useIsMobile();
  const tAccounts = useTranslations("accounts");
  const { addReturnToParam } = useReturnTo();
  const { isAccountSuspended } = useCustomerAccount();

  // Check if account has balance for pay and exchange
  const numericBalance =
    typeof balance === "string" ? parseFloat(balance) : balance;
  const hasBalance = numericBalance > 0;

  // Check if currency is BTC
  const isBTC = currency === "BTC";
  // Suspended (view-only) accounts keep the buttons visible but disabled, so
  // the layout is stable and the tooltip explains why.
  const canPay =
    !isAccountSuspended && (forceEnablePay || (isPayAvailable && hasBalance));
  const canExchange = !isAccountSuspended && isExchangeAvailable && !isBTC;

  return (
    <div className="rounded-lg w-full flex flex-col justify-between pb-2 gap-3">
      <div className="flex flex-row items-center gap-3 lg:gap-4 justify-center">
        {showCurrencyFlag && (
          <CurrencyFlag currency={currency} size={isMobile ? "32px" : "36px"} />
        )}
        <div className="flex flex-col items-center text-center gap-1">
          {/* Only render the label row when there is something to show — an
              empty p keeps its min-h-7 and adds dead space between the account
              page's currency dropdown and the balance (no totalBalanceText
              there). min-h-7 stays for callers that do pass content, so the
              row height is stable with or without the small flag. */}
          {(totalBalanceText || showSmallCurrencyFlag) && (
            <p className="text-lg md:text-xl font-semibold flex items-center gap-2 flex-row min-h-7">
              {showSmallCurrencyFlag && (
                <CurrencyFlag
                  currency={currency}
                  size={isMobile ? "20px" : "24px"}
                />
              )}
              {totalBalanceText}
            </p>
          )}
          <div className="flex h-11 md:h-12 items-center justify-center py-1">
            {isStillCalculating ? (
              <Skeleton className="h-9 md:h-10 w-48 lg:w-64" />
            ) : (
              <h2 className="text-3xl md:text-4xl font-semibold leading-none">
                <AnimatedNumber value={balance} currency={currency} />
              </h2>
            )}
          </div>
          {subtitleText && (
            <p className="ml-1 text-sm md:text-base font-medium text-muted-foreground">
              {subtitleText}
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-row gap-10 justify-center">
        {canPay ? (
          <ActionIconButton
            icon={ArrowUp}
            label={
              <>
                <span>{tAccounts("makePaymentLine1")}</span>
                <span>{tAccounts("makePaymentLine2")}</span>
              </>
            }
            href={addReturnToParam(
              getPayRoute(!isBaseCurrency ? currency : "")
            )}
            strokeWidth={3}
          />
        ) : (
          <ActionIconButton
            icon={ArrowUp}
            label={
              <>
                <span>{tAccounts("makePaymentLine1")}</span>
                <span>{tAccounts("makePaymentLine2")}</span>
              </>
            }
            disabled
            tooltipText={
              isAccountSuspended
                ? tAccounts("suspendedViewOnly")
                : !isPayAvailable
                  ? tAccounts("payNotAvailable")
                  : tAccounts("insufficientBalance")
            }
            strokeWidth={3}
          />
        )}

        {canExchange ? (
          <ActionIconButton
            icon={Repeat}
            label={
              <>
                <span>{tAccounts("currencyExchangeLine1")}</span>
                <span>{tAccounts("currencyExchangeLine2")}</span>
              </>
            }
            href={addReturnToParam(
              hasBalance
                ? getExchangeSellRoute(currency)
                : getExchangeBuyRoute(currency)
            )}
          />
        ) : (
          <ActionIconButton
            icon={Repeat}
            label={
              <>
                <span>{tAccounts("currencyExchangeLine1")}</span>
                <span>{tAccounts("currencyExchangeLine2")}</span>
              </>
            }
            disabled
            tooltipText={
              isAccountSuspended
                ? tAccounts("suspendedViewOnly")
                : isBTC
                  ? tAccounts("otcOnly")
                  : tAccounts("exchangeNotAvailable")
            }
          />
        )}

        {isDepositAvailable ? (
          <DepositButton
            variant={depositButtonVariant}
            showAccountSelect={showDepositAccountSelect}
            accountId={currency}
          />
        ) : (
          <ActionIconButton
            icon={Landmark}
            label={
              <>
                <span>{tAccounts("accountDetailsLine1")}</span>
                <span>{tAccounts("accountDetailsLine2")}</span>
              </>
            }
            disabled
            tooltipText={tAccounts("depositNotAvailable")}
          />
        )}
      </div>
    </div>
  );
}
